// Deal explorer — sortable table + CSV export.

import { fetchHealthSignals } from "../api";
import { getState, setFilters, setShowTagsColumn, setSort } from "../state";
import type { Filters, OpportunityRow, SortState } from "../types";
import { TAG_CONFIDENCE_THRESHOLD } from "../lib/aggregations";
import { esc } from "../lib/format";
import { renderMaturityBadge } from "../lib/maturity";
import { signalLabel } from "../lib/signals";
import { openTagPopover } from "../lib/tag_popover";
// SGNL-205: saved-view chip strip above the deals table. Rendered
// here so it's visible whenever the Deal Explorer is the active tab;
// chip clicks update state.filters + URL hash via deal_chips.ts.
import { bindChipStrip, currentChipView, renderChipStrip } from "./deal_chips";

function renderSignalBadges(slugs: string[], oppId: string): string {
  if (!slugs.length) return "—";
  return slugs
    .map(
      (s) =>
        // Single style= attr — HTML parser discards duplicate attributes
        // (keeps first, drops second) so cursor+font+border must be merged.
        // Architect-review HIGH on PR #85.
        `<span class="chip signal-chip" ` +
        `style="font-size:0.75em; border:1px solid var(--line); background:transparent; cursor:pointer;" ` +
        `data-testid="deals-signal-chip" data-signal="${esc(s)}" data-opp-id="${esc(oppId)}" ` +
        `role="button" tabindex="0" title="Filter to ${esc(signalLabel(s))}">${esc(signalLabel(s))}</span>`,
    )
    .join(" ");
}

const fmtMoney = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtPct = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  return `${n.toFixed(0)}%`;
};

// SGNL-70 — render the [0,1] predicted_score as an integer 0–100 so it
// reads on the same scale as Prob% beside it. null = unscored (closed
// opp, or open opp not yet scored) → em-dash. A score at/above
// HIGH_SCORE_THRESHOLD (on the 0–100 scale) gets a highlight class so
// the strongest open deals pop in a scan.
const HIGH_SCORE_THRESHOLD = 60;
const fmtScore = (n: number | null | undefined): number | null => {
  if (n === null || n === undefined) return null;
  return Math.round(n * 100);
};

// SGNL-78 / SGNL-97 — column order (accountability left, detail right) and
// the AI ✓/✗ merge into ai_maturity. Tags is opt-in via state.showTagsColumn.
//
// SGNL-108 / CR-Q3 on PR #54: `Column` is a discriminated pair so a
// "synthetic" CSV-only column (no underlying `OpportunityRow` field —
// e.g. derived ai_maturity_rep_set / ai_maturity_suggestion) doesn't
// need to lie via `as keyof OpportunityRow`. The runtime gate is
// `c.syntheticKey != null`, which TypeScript can prove safe.
type RealColumn = {
  key: keyof OpportunityRow;
  syntheticKey?: undefined;
  label: string;
  numeric?: boolean;
};
type SyntheticColumn = {
  key?: undefined;
  syntheticKey: string;
  label: string;
  numeric?: boolean;
};
type Column = RealColumn | SyntheticColumn;
const BASE_COLUMNS: RealColumn[] = [
  { key: "account_name", label: "Account" },
  { key: "vp", label: "Portfolio Lead" },
  { key: "owner_name", label: "Opp Owner" },
  { key: "name", label: "Opportunity" },
  { key: "probability", label: "Prob%", numeric: true },
  // SGNL-70 — rule-based predictive score, 0–100. Sits right of Prob%
  // so an operator can compare the rep's stated probability against the
  // model's conversion-likelihood signal in reading order.
  { key: "predicted_score", label: "Score", numeric: true },
  { key: "stage", label: "Stage" },
  { key: "close_date", label: "Close date" },
  { key: "closed_out_date", label: "Closed out" },
  { key: "cy_amount", label: "CY Rev", numeric: true },
  { key: "f_cy_amount", label: "F CY Rev", numeric: true },
  { key: "amount", label: "Amount", numeric: true },
  { key: "type", label: "Type" },
  { key: "bu", label: "BU" },
  // SGNL-268 — Opportunity Information (how did we get this opp). Opportunity
  // Source (e.g. "Anthropic") is the ANT-enablement dimension; also filterable.
  { key: "opportunity_source", label: "Opp Source" },
  { key: "opportunity_medium", label: "Opp Medium" },
  { key: "is_influenced", label: "Influenced" },
  { key: "referral_details", label: "Referral Details" },
  // SGNL-270 — raw ';'-delimited Project_Platform__c; drives the Platform and
  // Platform Family filters.
  { key: "project_platform", label: "Platform" },
  { key: "ai_maturity", label: "AI Maturity" },
  // SGNL-127 — LLM-derived "selling AI" / "using AI" columns sourced
  // from opportunity_tags (verdict='yes' AND confidence >= server-side
  // threshold). Sit RIGHT of the rep-tagged AI Maturity so an operator
  // scanning a row sees Rep → LLM verdict in reading order. Hover the
  // ✓ to see the LLM's rationale (the "why?" tooltip).
  { key: "llm_ai_solution", label: "Selling AI (LLM)" },
  { key: "llm_ai_delivery", label: "Using AI (LLM)" },
  // SGNL-153 — health-signal badge column. active_signals is populated
  // client-side after the post-render bulk fetch; initially undefined
  // (renders "—" until fetch resolves).
  { key: "active_signals", label: "Signals" },
];
const TAGS_COLUMN: RealColumn = { key: "tags", label: "Tags" };

// SGNL-108 — CSV emits the AI Maturity rep-set value and the LLM
// suggestion as TWO separate columns rather than a single pipe-joined
// cell. A future SF picklist value containing a `|` would otherwise
// silently corrupt downstream analyst splits. Two columns are also
// easier to pivot / filter / colour-code in spreadsheets. The on-screen
// table keeps the combined "rep + chip" rendering — this only affects
// CSV export.
const AI_MATURITY_REP_CSV_COLUMN: SyntheticColumn = {
  syntheticKey: "ai_maturity_rep_set",
  label: "AI Maturity (rep)",
};
const AI_MATURITY_SUG_CSV_COLUMN: SyntheticColumn = {
  syntheticKey: "ai_maturity_suggestion",
  label: "AI Maturity (suggested)",
};
// Replace the combined `ai_maturity` column in CSV with the split pair
// at the same position, preserving column order.
// `active_signals` is client-side only (not in the bundle) and
// unpredictably multi-valued — exclude from CSV to avoid Array.toString()
// producing comma-joined slugs that break column alignment. Architect-
// review MEDIUM on PR #85.
const CSV_COLUMNS: Column[] = [
  ...BASE_COLUMNS.flatMap((c) => {
    if (c.key === "ai_maturity")
      return [AI_MATURITY_REP_CSV_COLUMN, AI_MATURITY_SUG_CSV_COLUMN] as Column[];
    if (c.key === "active_signals") return [] as Column[];
    return [c as Column];
  }),
  TAGS_COLUMN,
];

// On-screen render uses `c.key` directly (no synthetic columns) so the
// return type is narrowed to `RealColumn[]`. Synthetic columns are
// CSV-only — they're spliced into `CSV_COLUMNS`, never into
// `activeColumns()`.
function activeColumns(): RealColumn[] {
  return getState().showTagsColumn ? [...BASE_COLUMNS, TAGS_COLUMN] : BASE_COLUMNS;
}

// Defuse Excel/Sheets formula injection. Guard cells whose first
// non-whitespace character is = + - @ (Excel evaluates them even when
// preceded by spaces/tabs/CRs); prepend a single quote so the
// spreadsheet treats the cell as text. Quote on \r as well — bare CRs
// can split a row in some spreadsheet parsers.
// SGNL-211: moved to ../lib/csv. Re-exported here so existing callers
// (tests, hygiene before this PR) continue to work without churn.
// The new shared implementation extends the formula-guard regex to
// catch Unicode whitespace prefixes (NBSP / narrow NBSP) per the
// SGNL-211 security review.
import { csvCell as _csvCell, downloadCsv } from "../lib/csv";
import { getFocusListBand } from "../lib/focus_list_config";
export const csvCell = _csvCell;

// Map a tag verdict to the chip palette already defined in styles.css —
// same convention as the Opp Detail page tag chips so a tag rendered here
// and on the detail page looks identical.
function chipClassForVerdict(verdict: string): string {
  if (verdict === "yes") return "chip aqua";
  if (verdict === "no") return "chip coral";
  if (verdict === "maybe") return "chip indigo";
  return "chip blue";
}

function sortRows(rows: OpportunityRow[]): OpportunityRow[] {
  const { field, dir } = getState().sort;
  const factor = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = a[field];
    const vb = b[field];
    if (va === vb) return 0;
    if (va === null || va === undefined) return 1;
    if (vb === null || vb === undefined) return -1;
    if (va < vb) return -1 * factor;
    if (va > vb) return 1 * factor;
    return 0;
  });
}

function renderCell(r: OpportunityRow, key: keyof OpportunityRow): string {
  const v = r[key];
  if (key === "amount" || key === "cy_amount" || key === "f_cy_amount") {
    return fmtMoney(v as number | null);
  }
  if (key === "probability") return fmtPct(v as number | null);
  // SGNL-70 — predictive score as 0–100; high scores get a highlight.
  if (key === "predicted_score") {
    const score = fmtScore(v as number | null);
    if (score === null) return `<span class="muted">—</span>`;
    const hot = score >= HIGH_SCORE_THRESHOLD ? " deals-score--high" : "";
    return `<span class="deals-score${hot}" data-testid="deals-score">${score}</span>`;
  }
  // SGNL-97 + SGNL-98 — ai_boolean=false → "Non-AI"; otherwise rep-set
  // value plus always-on suggestion badge (operators audit calibration
  // every row, not just on disagreement).
  if (key === "ai_maturity") {
    if (r.ai_boolean === false) {
      return `<span class="muted">Non-AI</span>`;
    }
    const rep = v ? esc(String(v)) : "";
    const badge = renderMaturityBadge(
      r.maturity_suggestion,
      "deals-maturity-badge",
    );
    if (!rep && !badge) return "—";
    return rep && badge ? `${rep} ${badge}` : (rep || badge);
  }
  // SGNL-127 — LLM-derived AI columns. Both are bool with an optional
  // rationale carried alongside on the row. ✓ when true, "—" when
  // false; `title=` carries the rationale text so a hover surfaces the
  // LLM's reasoning. We deliberately use the native `title` attribute
  // rather than a custom popover so screen readers and keyboard users
  // get the rationale for free (browsers expose `title` as the
  // accessible description). esc() guards against angle brackets in
  // the rationale text.
  if (key === "llm_ai_solution" || key === "llm_ai_delivery") {
    const isTrue = v === true;
    if (!isTrue) return `<span class="muted">—</span>`;
    const rationale =
      key === "llm_ai_solution"
        ? r.llm_ai_solution_rationale
        : r.llm_ai_delivery_rationale;
    const tipAttr = rationale
      ? ` title="${esc(rationale)}"`
      : ` title="LLM classified this opportunity as ${key === "llm_ai_solution" ? "Selling AI" : "Using AI"} but did not record a rationale."`;
    return (
      `<span class="chip aqua" data-testid="deals-${key.replace(/_/g, "-")}-chip"` +
      `${tipAttr} style="cursor:help;">✓</span>`
    );
  }
  // SGNL-153 — health-signal badge cluster. Rendered from the client-side
  // active_signals[] field populated by hydrateSignalBadges() after the
  // table's initial render. undefined → "—" (not yet fetched); []→ "—"
  // (clean opp). Badges are clickable to set the activeSignals filter.
  if (key === "active_signals") {
    const slugs = (v as string[] | undefined) ?? [];
    return renderSignalBadges(slugs, r.id);
  }
  // SGNL-84 — opp name links to the new detail page. Using the existing
  // `.drill` style already used for tag/account drill cells in this
  // table, so visual treatment is consistent (no new class).
  if (key === "name") {
    return `<a class="drill" href="#opp/${esc(r.id)}" data-testid="deals-opp-link">${esc(String(v))}</a>`;
  }
  // SGNL-26 — tag chips. Show only verdict='yes' tags above the active
  // SGNL-109 — threshold is now a single named constant
  // TAG_CONFIDENCE_THRESHOLD; the prior operator-tunable value lived
  // in filters.tagThreshold which got removed.
  if (key === "tags") {
    const tags = (r.tags ?? []).filter(
      (t) => t.verdict === "yes" && t.confidence >= TAG_CONFIDENCE_THRESHOLD,
    );
    if (tags.length === 0) return "—";
    // SGNL-99 — chip click → popover with verdict rationale.
    // `data-opp-id` + `data-tag-key` attrs let the delegated click
    // handler look up the rationale via lazy-fetch of /detail.
    return tags
      .map(
        (t) =>
          `<span class="${chipClassForVerdict(t.verdict)}" data-testid="deals-tag-chip" ` +
          `role="button" tabindex="0" ` +
          `data-opp-id="${esc(r.id)}" data-tag-key="${esc(t.tag_key)}" ` +
          `style="cursor:pointer;" ` +
          `title="Click for verdict rationale">${esc(t.tag_key)}</span>`,
      )
      .join(" ");
  }
  if (v === null || v === undefined) return "—";
  // Escape — Salesforce string fields can contain quotes/HTML.
  return esc(String(v));
}

export function toCsv(rows: OpportunityRow[]): string {
  // CSV always includes Tags + AI Maturity (CSV_COLUMNS) regardless of the
  // on-screen toggle — it's for downstream analysis. Every cell goes
  // through csvCell() for formula-injection + special-char quoting.
  const lines = [CSV_COLUMNS.map((c) => c.label).join(",")];
  for (const r of rows) {
    const line = CSV_COLUMNS.map((c) => {
      // SGNL-105 — comma-separated yes-only tag_keys (was dense
      // "key:verdict@conf;..." dump). Multi-tag joins always contain a
      // comma so csvCell will quote them.
      if (c.key === "tags") {
        const yesTags = (r.tags ?? [])
          .filter((t) => t.verdict === "yes")
          .map((t) => t.tag_key);
        return csvCell(yesTags.join(", "));
      }
      // SGNL-108 — TWO separate columns instead of a `|`-joined cell.
      // SGNL-29 + SGNL-97 still apply: ai_boolean=false → "Non-AI" in
      // the rep column; suggestion column is empty in that case
      // (model wouldn't run for a known-Non-AI deal anyway).
      if (c.syntheticKey === "ai_maturity_rep_set") {
        if (r.ai_boolean === false) return csvCell("Non-AI");
        const repSet = r.ai_maturity;
        return repSet != null && repSet !== "" ? csvCell(repSet) : "";
      }
      if (c.syntheticKey === "ai_maturity_suggestion") {
        if (r.ai_boolean === false) return "";
        const s = r.maturity_suggestion;
        // PR-Agent T3 on PR #54: a runtime-tampered `confidence` that
        // is a string would crash `.toFixed(2)`. Coerce both fields
        // once + skip the row's suggestion cell entirely if either is
        // non-finite (CSV gets "" instead of a partial / NaN-bearing
        // cell). Format remains "Stage N@0.87" for grep-ability.
        if (!s) return "";
        const stageNum = Number(s.suggested_stage);
        const confNum = Number(s.confidence);
        if (!Number.isFinite(stageNum) || !Number.isFinite(confNum)) return "";
        return csvCell(`Stage ${stageNum}@${confNum.toFixed(2)}`);
      }
      // After this point only RealColumn (with `c.key`) is reachable —
      // the synthetic columns above always early-return.
      if (c.key === undefined) return "";
      const v = r[c.key];
      // SGNL-127 — the LLM AI columns are booleans; render "yes" / ""
      // so analysts can filter on a single literal rather than parse
      // "true"/"false". Skipping the rationale text in CSV is
      // intentional — for that, hit the Opp Detail tag chips or the
      // /detail API. Keeps the CSV column count predictable.
      if (c.key === "llm_ai_solution" || c.key === "llm_ai_delivery") {
        return v === true ? csvCell("yes") : "";
      }
      // SGNL-70 — export the 0–100 integer so the CSV matches the
      // on-screen Score column rather than the raw [0,1] float.
      if (c.key === "predicted_score") {
        const score = fmtScore(v as number | null);
        return score === null ? "" : csvCell(String(score));
      }
      if (v === null || v === undefined) return "";
      return csvCell(String(v));
    }).join(",");
    lines.push(line);
  }
  return lines.join("\n");
}

// SGNL-193 — Focus list toggle state.
//
// Q-01 / ARCH-01 fix: instead of detecting "focus active" by deep-
// equality on the four governed filter/sort keys (which has false
// positives when the user coincidentally arrives at the same values
// and false negatives when they edit one key independently), we
// stash the pre-click state in a module-level variable on activate
// and restore from it on deactivate. The button's pressed state is
// then `_focusRestore !== null` — authoritative, not derived.
//
// Q-04 / ARCH-04 fix: capturing the prior `sort` too means the
// deactivate path restores the user's previous sort, not a hard-
// coded amount/desc default.
//
// Exported for testability (T-02).
type FocusRestore = {
  filters: Pick<Filters, "state" | "probability_min" | "probability_max" | "min_amount">;
  sort: SortState;
};
let _focusRestore: FocusRestore | null = null;

export function isFocusActive(): boolean {
  return _focusRestore !== null;
}

/** Test-only seam — reset toggle state between tests. Not used in prod. */
export function _resetFocusForTests(): void {
  _focusRestore = null;
}


export function renderDealsTab(host: HTMLElement, rows: OpportunityRow[]): void {
  const sorted = sortRows(rows);
  const { field, dir } = getState().sort;
  const cols = activeColumns();
  host.innerHTML = `
    <div class="tab-content" data-testid="deals-tab">
      ${renderChipStrip(currentChipView())}
      <div class="tab-header">
        <h2>Deal explorer <span class="row-count" data-testid="row-count">${sorted.length.toLocaleString()} rows</span></h2>
        <div style="display:flex; gap:8px; align-items:center;">
          ${(() => {
            // SGNL-209: aria-label reads the configured band so screen
            // readers announce the actual filter being applied.
            const band = getFocusListBand();
            return `<button type="button" class="btn-secondary" data-action="focus-list"
                  data-testid="focus-list-btn"
                  aria-pressed="${isFocusActive()}"
                  aria-label="Toggle Focus list: open deals with probability ${band.min}%–${band.max}% and revenue, sorted by probability">
            ${isFocusActive() ? "✓ Focus list" : "Focus list"}
          </button>`;
          })()}
          <button type="button" class="btn-secondary" data-action="toggle-tags"
                  data-testid="toggle-tags-col"
                  aria-pressed="${getState().showTagsColumn}">
            ${getState().showTagsColumn ? "Hide" : "Show"} tags
          </button>
          <button type="button" class="btn-secondary" data-action="csv" data-testid="csv-export">Export CSV</button>
        </div>
      </div>
      <div class="table-wrap table-wrap--scrollable">
        <table class="data-table sortable">
          <thead>
            <tr>
              ${cols
                .map(
                  (c) => `
                <th class="${c.numeric ? "num" : ""} ${c.key === field ? `sorted-${dir}` : ""}"
                    data-sort-key="${c.key}">${c.label}</th>`,
                )
                .join("")}
            </tr>
          </thead>
          <tbody>
            ${sorted.length === 0
              ? `<tr><td colspan="${cols.length}" style="text-align:center;padding:32px;color:var(--slate);font-style:italic;">No opportunities match the current filters.</td></tr>`
              : sorted
                  .slice(0, 500)
                  .map(
                    (r) => `
              <tr data-opp-id="${esc(r.id)}">
                ${cols.map((c) => `<td class="${c.numeric ? "num" : ""}" data-col="${c.key}">${renderCell(r, c.key)}</td>`).join("")}
              </tr>`,
                  )
                  .join("")}
          </tbody>
        </table>
      </div>
      ${sorted.length > 500 ? `<p class="table-footnote">Showing first 500 of ${sorted.length.toLocaleString()} rows. Filter to narrow further or use CSV export for the full set.</p>` : ""}
      ${sorted.length > 100 ? `<p class="table-footnote">Signal badges shown for the top 100 rows. Narrow with filters for full badge coverage.</p>` : ""}
    </div>
  `;
  // SGNL-205: bind chip clicks BEFORE any other handler so the URL +
  // state are in sync ahead of any subsequent subscribe-driven re-render.
  bindChipStrip(host);
  // SGNL-97 — Tags column visibility toggle. setShowTagsColumn() lives on
  // the shared store, so any other subscriber that re-renders the tab
  // sees the same flag without relying on module-local closure state.
  // When hiding the column, reset sort if the user was sorting on tags
  // — otherwise the table stays sorted by an invisible key with no
  // visual indicator.
  host
    .querySelector<HTMLButtonElement>('[data-action="toggle-tags"]')
    ?.addEventListener("click", () => {
      const nextVisible = !getState().showTagsColumn;
      if (!nextVisible && getState().sort.field === "tags") {
        setSort({ field: "amount", dir: "desc" });
      }
      setShowTagsColumn(nextVisible);
    });
  // SGNL-193 — Focus list one-click preset. Captures pre-click
  // filters + sort in _focusRestore, applies the canonical "deals worth
  // attention" preset. Second click restores from _focusRestore so the
  // user's pre-focus sort and any other filter values are intact.
  host
    .querySelector<HTMLButtonElement>('[data-action="focus-list"]')
    ?.addEventListener("click", () => {
      if (isFocusActive()) {
        const restore = _focusRestore;
        _focusRestore = null;
        if (restore) {
          setFilters(restore.filters);
          setSort(restore.sort);
        } else {
          // Fallback (e.g. focus was activated before this code path existed)
          setFilters({
            state: undefined,
            probability_min: undefined,
            probability_max: undefined,
            min_amount: undefined,
          });
          setSort({ field: "amount", dir: "desc" });
        }
      } else {
        const st = getState();
        _focusRestore = {
          filters: {
            state: st.filters.state,
            probability_min: st.filters.probability_min,
            probability_max: st.filters.probability_max,
            min_amount: st.filters.min_amount,
          },
          sort: st.sort,
        };
        // SGNL-209: band is config-driven (VITE_FOCUS_LIST_PROBABILITY_MIN/MAX
        // env vars, default {50, 90}). Replaces the hardcoded `50`.
        const band = getFocusListBand();
        setFilters({
          state: "open",
          probability_min: band.min,
          probability_max: band.max,
          min_amount: 1,
        });
        setSort({ field: "probability", dir: "desc" });
      }
    });
  // Sort handlers
  for (const th of host.querySelectorAll<HTMLTableCellElement>("th[data-sort-key]")) {
    th.addEventListener("click", () => {
      const key = th.dataset.sortKey as keyof OpportunityRow;
      const current = getState().sort;
      const nextDir: "asc" | "desc" =
        current.field === key && current.dir === "desc" ? "asc" : "desc";
      setSort({ field: key, dir: nextDir });
    });
  }
  // SGNL-99 — chip click → tag verdict popover. Delegated handler on
  // the table tbody so chips added by future re-renders are covered
  // automatically (no per-chip listener bookkeeping).
  host.querySelector<HTMLElement>("tbody")?.addEventListener("click", (ev) => {
    const target = (ev.target as HTMLElement).closest<HTMLElement>(
      '[data-testid="deals-tag-chip"][data-opp-id][data-tag-key]',
    );
    if (!target) return;
    ev.stopPropagation(); // Prevent the row's own drill-through handler.
    const oppId = target.dataset.oppId;
    const tagKey = target.dataset.tagKey;
    if (!oppId || !tagKey) return;
    void openTagPopover(target, oppId, tagKey);
  });
  // Keyboard accessibility — Enter/Space on a focused chip behaves
  // like a click.
  host.querySelector<HTMLElement>("tbody")?.addEventListener("keydown", (ev) => {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    const target = (ev.target as HTMLElement).closest<HTMLElement>(
      '[data-testid="deals-tag-chip"][data-opp-id][data-tag-key]',
    );
    if (!target) return;
    ev.preventDefault();
    const oppId = target.dataset.oppId;
    const tagKey = target.dataset.tagKey;
    if (!oppId || !tagKey) return;
    void openTagPopover(target, oppId, tagKey);
  });
  // SGNL-153 — signal badge click → toggle that signal in the activeSignals
  // filter (same OR-select pattern as tagKeys). Signal chips are rendered
  // lazily by hydrateSignalBadges after the bulk fetch; the delegated handler
  // here fires on chips already in the DOM (populated state) as well as any
  // added by a re-render triggered by the filter change.
  host.querySelector<HTMLElement>("tbody")?.addEventListener("click", (ev) => {
    const chip = (ev.target as HTMLElement).closest<HTMLElement>(
      '[data-testid="deals-signal-chip"][data-signal]',
    );
    if (!chip) return;
    ev.stopPropagation();
    const signal = chip.dataset.signal;
    if (!signal) return;
    const current = getState().filters.activeSignals ?? [];
    const next = current.includes(signal)
      ? current.filter((s) => s !== signal)
      : [...current, signal];
    setFilters({ activeSignals: next.length ? next : undefined });
  });
  host.querySelector<HTMLElement>("tbody")?.addEventListener("keydown", (ev) => {
    if (ev.key !== "Enter" && ev.key !== " ") return;
    const chip = (ev.target as HTMLElement).closest<HTMLElement>(
      '[data-testid="deals-signal-chip"][data-signal]',
    );
    if (!chip) return;
    ev.preventDefault();
    // Intentionally delegates to the click handler rather than duplicating
    // toggle logic. If the click delegation scope changes, update this too.
    chip.click();
  });

  // Kick off the bulk health-signals fetch for the visible rows (first 100).
  // Non-blocking — the table is already usable; badges fill in asynchronously.
  void hydrateSignalBadges(host, sorted.slice(0, 100));

  // CSV export — SGNL-211 extracted the Blob+anchor sequence to
  // lib/csv.ts:downloadCsv (shared with the Data Hygiene tab).
  host.querySelector<HTMLButtonElement>('[data-action="csv"]')?.addEventListener("click", () => {
    const csv = toCsv(sorted);
    downloadCsv(
      `signal-opportunities-${new Date().toISOString().slice(0, 10)}.csv`,
      csv,
    );
  });
}

// ---------------------------------------------------------------------------
// SGNL-153 — Health-signal badge hydration
// ---------------------------------------------------------------------------
//
// Fired after every renderDealsTab call. Fetches the bulk endpoint for the
// first 100 visible rows, mutates `row.active_signals` on the in-memory
// OpportunityRow objects (so applyFilters sees the signals on subsequent
// state changes), then updates the corresponding `<td data-col="active_signals">`
// cells in the already-rendered DOM.
//
// Non-fatal: if the endpoint is unavailable the table is still fully usable
// without signal badges. The mutation to row.active_signals is idempotent —
// a second call (e.g. after the filter changes and the tab re-renders) will
// overwrite with the same data from cache.

async function hydrateSignalBadges(
  host: HTMLElement,
  rows: OpportunityRow[],
): Promise<void> {
  if (!rows.length || !host.isConnected) return;
  // Only fetch rows that haven't been hydrated yet (active_signals undefined).
  const unfetched = rows.filter((r) => r.active_signals === undefined);
  if (!unfetched.length) {
    // All rows already hydrated — just re-render badges (filter changed).
    _updateBadgeCells(host, rows);
    return;
  }
  try {
    const result = await fetchHealthSignals(unfetched.map((r) => r.id));
    // Guard again — host may have been replaced by a tab switch while fetch
    // was in flight.
    if (!host.isConnected) return;
    for (const row of unfetched) {
      row.active_signals = result.signals[row.id]?.active_signals ?? [];
    }
  } catch (e) {
    if (!host.isConnected) return;
    // Log for debugging (e.g. over-cap 400, rate-limit 429) before
    // marking rows as empty so repeated render cycles don't retry.
    // Architect-review LOW on PR #85.
    console.error("[SGNL-153] hydrateSignalBadges fetch failed", e);
    for (const row of unfetched) {
      row.active_signals = [];
    }
  }
  // Update DOM cells for ALL rows (unfetched now have data; rest were already set).
  if (host.isConnected) _updateBadgeCells(host, rows);
}

function _updateBadgeCells(host: HTMLElement, rows: OpportunityRow[]): void {
  for (const row of rows) {
    // Avoid building a CSS selector string from row.id — even though SF
    // IDs are currently alphanumeric, hardening now prevents injection if
    // the ID origin ever changes. Walk all rows and match via dataset
    // equality instead. Architect/security HIGH on PR #85.
    const tr = Array.from(host.querySelectorAll<HTMLElement>("tr[data-opp-id]")).find(
      (el) => el.dataset.oppId === row.id,
    );
    const td = tr?.querySelector<HTMLElement>('td[data-col="active_signals"]');
    if (!td) continue;
    td.innerHTML = renderSignalBadges(row.active_signals ?? [], row.id);
  }
}
